import HeaderSection from '@/components/Header/HeaderSection.vue';
import PortfolioSidebar from "@/components/PortfolioSidebar/PortfolioSidebar.vue";
import PropertySidebar from "@/components/PropertySidebar/PropertySidebar.vue";
import sidebar_page_context from "@/components/sidebar_page_context";
import { get_user_profile, fetch_and_store_user_profile, get_token, set_user_profile } from '@/router';
import {
    build_url_with_query,
    ensure_trailing_slash,
    normalize_properties_response
} from "@/components/Settings/settings_utils";
import { filter_live_active_properties } from "@/components/Settings/property_access_utils";
import is_admin_user from "@/utils/auth_user";
import {
    assign_error,
    validate_person_name,
    validate_phone_number
} from "@/utils/form_validators";
import {
    fetch_user_property_assignments,
    get_assignment_properties,
    get_assignment_property_ids,
    sync_user_property_assignments
} from "@/utils/user_property_assignments";

// profile urls
const view_user_url = process.env.VUE_APP_VIEW_USER_URL;
const user_management_url = process.env.VUE_APP_USER_MANAGEMENT_URL || process.env.VUE_APP_VIEW_USER_URL;
const update_user_url = process.env.VUE_APP_UPDATE_USER_URL;
const properties_url = ensure_trailing_slash(process.env.VUE_APP_PROPERTIES_URL);
const client_app = process.env.VUE_APP_CLIENT_APP || "bcx";

export default {
    name: "EditProfile",

    mixins: [sidebar_page_context],

    components: {
        HeaderSection,
        PortfolioSidebar,
        PropertySidebar
    },

    data() {
        return ({
            breadcrumb: [
                { id: "home", title: "Dashboard" },
                { id: "profile", title: "Profile" },
                { id: "edit", title: "Edit Profile" }
            ],

            form: {
                first_name: "",
                last_name: "",
                email: "",
                phone: "",
                property_ids: [],
                location: "Gauteng, South Africa",
                bio: ""
            },
            property_options: [],
            is_loading_properties: false,
            is_loading: false,
            is_saving: false,
            form_error: "",
            snackbar_timeout_id: null,
            field_errors: {},
            view_user_url: view_user_url,
            user_management_url: user_management_url,
            update_user_url: update_user_url,
            properties_url: properties_url,
            is_mobile: window.innerWidth < 768,
            mobile_sidebar_open: false,
            sidebar_collapsed: true
        });
    },

    async mounted() {
        window.addEventListener("resize", this.handle_resize);
        this.set_breadcrumb();
        await this.load_properties();
        this.loadUserProfile();
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.handle_resize);
        this.clear_snackbar_timer();
    },

    computed: {
        selected_user_id() {
            return this.parse_user_id(this.$route.query.user_id);
        },

        is_managed_user_edit() {
            return Boolean(this.selected_user_id);
        },

        page_title() {
            return this.is_managed_user_edit ? "Edit User" : "Edit Profile";
        },

        page_description() {
            return this.is_managed_user_edit
                ? "Update this user's personal details and contact information."
                : "Update your personal details and keep your contact information current.";
        },

        avatar_initials() {
            const first = this.form.first_name?.charAt(0) || "";
            const last = this.form.last_name?.charAt(0) || "";
            return (first + last).toUpperCase() || "UR";
        },

        selected_property_count() {
            return this.form.property_ids.length;
        }
    },

    watch: {
        form_error(message) {
            if (message) {
                this.start_snackbar_timer();
            }
        }
    },

    methods: {
        handle_resize() {
            this.is_mobile = window.innerWidth < 768;
            if (!this.is_mobile) this.mobile_sidebar_open = false;
        },

        toggle_mobile_sidebar() {
            this.mobile_sidebar_open = !this.mobile_sidebar_open;
        },

        clear_snackbar_timer() {
            if (this.snackbar_timeout_id) {
                clearTimeout(this.snackbar_timeout_id);
                this.snackbar_timeout_id = null;
            }
        },

        start_snackbar_timer() {
            this.clear_snackbar_timer();
            this.snackbar_timeout_id = setTimeout(() => {
                this.form_error = "";
                this.snackbar_timeout_id = null;
            }, 5000);
        },

        clear_form_error() {
            this.clear_snackbar_timer();
            this.form_error = "";
        },

        handleBreadcrumbClick(id) {
            if (id === "home") {
                this.$router.push("/home");
            }
            if (id === "profile") {
                this.$router.push("/profile");
            }
            if (id === "user_management") {
                this.$router.push("/user_management");
            }
        },

        set_breadcrumb() {
            this.breadcrumb = this.is_managed_user_edit
                ? [
                    { id: "home", title: "Dashboard" },
                    { id: "user_management", title: "User Management" },
                    { id: "edit", title: "Edit User" }
                ]
                : [
                    { id: "home", title: "Dashboard" },
                    { id: "profile", title: "Profile" },
                    { id: "edit", title: "Edit Profile" }
                ];
        },

        set_profile_edit(profile = {}) {
            this.form.first_name = `${profile.first_name || ""}`.trim();
            this.form.last_name = `${profile.last_name || ""}`.trim();
            this.form.email = `${profile.email || ""}`.trim();
            this.form.phone = profile.phone_number || "";
            this.form.property_ids = this.get_profile_property_ids(profile);
        },

        get_profile_property_ids(profile = {}) {
            const property_ids = profile.property_ids
                || profile.assigned_property_ids
                || profile.profile?.property_ids
                || [];
            const properties = profile.assigned_properties
                || profile.properties
                || profile.property_assignments
                || profile.profile?.properties
                || [];
            const ids_from_properties = Array.isArray(properties)
                ? properties.map((property) => {
                    if (typeof property === "number" || typeof property === "string") {
                        return property;
                    }

                    return property?.property_id ?? property?.id ?? "";
                })
                : [];
            const ids = [
                ...(Array.isArray(property_ids) ? property_ids : []),
                ...ids_from_properties
            ];

            return ids
                .filter((property_id) => property_id !== "")
                .map((property_id) => String(property_id))
                .filter((property_id) => this.is_live_property_id(property_id))
                .filter((property_id, index, all_ids) => all_ids.indexOf(property_id) === index);
        },

        is_live_property_id(property_id) {
            if (!this.property_options.length) {
                return false;
            }

            return this.property_options.some((property) => String(property.id) === String(property_id));
        },

        is_property_selected(property_id) {
            return this.form.property_ids.some((selected_id) => String(selected_id) === String(property_id));
        },

        toggle_property(property_id) {
            const normalized_property_id = String(property_id);

            this.field_errors.property_ids = "";
            this.form.property_ids = this.is_property_selected(normalized_property_id)
                ? this.form.property_ids.filter((selected_id) => String(selected_id) !== normalized_property_id)
                : [...this.form.property_ids, normalized_property_id];
        },

        parse_user_id(raw_user_id) {
            const user_id_text = String(raw_user_id || "").trim();

            if (!/^\d+$/.test(user_id_text)) {
                return null;
            }

            const user_id = Number(user_id_text);
            return Number.isSafeInteger(user_id) && user_id > 0 ? user_id : null;
        },

        get_selected_profile_id(profile) {
            if (this.selected_user_id) {
                return this.selected_user_id;
            }

            return this.parse_user_id(profile && (profile.id || profile.user_id));
        },

        get_endpoint_url(base_url, user_id, action = "") {
            const safe_user_id = this.parse_user_id(user_id);
            const normalized_base_url = String(base_url || "").trim();

            if (!safe_user_id || !normalized_base_url) {
                return "";
            }

            const base_with_slash = normalized_base_url.endsWith("/")
                ? normalized_base_url
                : `${normalized_base_url}/`;
            const safe_action = action === "update" ? "update/" : "";

            return `${base_with_slash}${safe_user_id}/${safe_action}`;
        },

        get_auth_headers(include_json_content_type = true) {
            const auth_token = get_token();
            const headers = {
                "Accept": "application/json"
            };

            if (include_json_content_type) {
                headers["Content-Type"] = "application/json";
            }

            if (auth_token) {
                headers.Authorization = `Token ${auth_token}`;
            }

            return headers;
        },

        extract_collection(response_data, collection_keys = ["data", "results", "users"]) {
            if (Array.isArray(response_data)) {
                return response_data;
            }

            if (!response_data || typeof response_data !== "object") {
                return [];
            }

            for (const key of collection_keys) {
                const value = response_data[String(key)];

                if (Array.isArray(value)) {
                    return value;
                }

                if (value && typeof value === "object") {
                    const nested_collection = this.extract_collection(value, collection_keys);

                    if (nested_collection.length) {
                        return nested_collection;
                    }
                }
            }

            return [];
        },

        find_user_by_id(response_data, user_id) {
            const safe_user_id = this.parse_user_id(user_id);
            const users = this.extract_collection(response_data);

            if (users.length) {
                return users.find((user) => this.parse_user_id(user?.id || user?.user_id) === safe_user_id) || null;
            }

            return this.parse_user_id(response_data?.id || response_data?.user_id) === safe_user_id
                ? response_data
                : null;
        },

        get_selected_user_from_storage() {
            return JSON.parse(sessionStorage.getItem("edit_user_profile") || "null");
        },

        get_property_options_from_storage() {
            return JSON.parse(sessionStorage.getItem("edit_user_property_options") || "[]");
        },

        async load_properties() {
            this.is_loading_properties = true;

            try {
                const stored_property_options = this.get_property_options_from_storage();

                if (Array.isArray(stored_property_options) && stored_property_options.length) {
                    this.property_options = filter_live_active_properties(stored_property_options);
                    return;
                }

                if (!this.properties_url || !get_token()) {
                    this.property_options = [];
                    return;
                }

                const response = await fetch(build_url_with_query(this.properties_url, { client_app }), {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                });
                const response_data = await this.get_response_data(response, "Unable to load properties.");
                const properties = this.extract_collection(response_data, ["data", "results", "properties"]);

                this.property_options = filter_live_active_properties(normalize_properties_response(properties));
            } catch {
                this.property_options = [];
            } finally {
                this.is_loading_properties = false;
            }
        },

        get_return_path() {
            return this.$route.query.return_to === "user_management"
                ? "/user_management"
                : "/profile";
        },

        get_error_message(response_data, fallback_message) {
            const error_data = response_data?.errors
                || response_data?.detail
                || response_data?.error
                || response_data?.message
                || response_data;

            if (!error_data) return fallback_message;
            if (typeof error_data === "string") return error_data;
            if (Array.isArray(error_data)) return error_data.join(", ");

            if (typeof error_data === "object") {
                return Object.entries(error_data)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`)
                    .join(" | ");
            }

            return fallback_message;
        },

        async get_response_data(response, fallback_message) {
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(this.get_error_message(data, fallback_message));
            }

            return data;
        },

        async load_managed_user(user_id) {
            const safe_user_id = this.parse_user_id(user_id);
            const stored_user = this.get_selected_user_from_storage();
            const stored_user_id = this.parse_user_id(stored_user && (stored_user.id || stored_user.user_id));

            if (stored_user && stored_user_id === safe_user_id) {
                return this.enrich_profile_with_property_assignments(stored_user, safe_user_id);
            }

            if (!safe_user_id) {
                throw new Error("Unable to load user details.");
            }

            throw new Error("Unable to load user details.");
        },

        async enrich_profile_with_property_assignments(profile, user_id) {
            const assignments = await fetch_user_property_assignments(user_id, this.get_auth_headers(false), {
                include_inactive: true
            });
            const live_property_ids = this.property_options.map((property) => String(property.id));
            const active_assignments = assignments.filter((assignment) => {
                return assignment.is_active && live_property_ids.includes(String(assignment.property_id));
            });

            return ({
                ...profile,
                property_assignments: assignments,
                property_ids: get_assignment_property_ids(active_assignments),
                assigned_properties: get_assignment_properties(active_assignments, this.property_options)
            });
        },

        async load_profile_phone(profile, user_id) {
            const safe_user_id = this.parse_user_id(user_id);

            if (!safe_user_id || !this.update_user_url || !get_token()) {
                if (!this.update_user_url) {
                    console.warn("VUE_APP_UPDATE_USER_URL is not configured.");
                }
                return profile;
            }

            try {
                const profile_request = new Request(this.get_endpoint_url(this.update_user_url, safe_user_id), {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                });
                const response = await fetch(profile_request);

                if (response.status === 200) {
                    const response_data = await response.json();
                    const profile_data = this.find_user_by_id(response_data, safe_user_id) || response_data;
                    profile.phone_number = profile_data.phone_number;
                }
            } catch (error) {
                console.log("Failed to fetch phone number: " + error);
            }

            return profile;
        },

        async loadUserProfile() {
            this.is_loading = true;
            this.form_error = "";

            try {
                let profile = get_user_profile();
                const auth_token = get_token();

                if (this.is_managed_user_edit) {
                    profile = await this.load_managed_user(this.selected_user_id);
                } else if (!profile) {
                    profile = await fetch_and_store_user_profile();
                }

                if (profile) {
                    const user_id = this.get_selected_profile_id(profile);

                    if (user_id && auth_token) {
                        profile = await this.load_profile_phone(profile, user_id);
                    }

                    this.set_profile_edit(profile);
                } else {
                    throw new Error("Failed to load user profile");
                }
            } catch (error) {
                this.form_error = error.message || "Failed to load profile data. Please try again.";
                console.error("Profile loading error:", error);

                if (!this.is_managed_user_edit) {
                    this.set_profile_edit(get_user_profile());
                }
            } finally {
                this.is_loading = false;
            }
        },

        cancelEdit() {
            this.$router.push(this.get_return_path());
        },

        validate_user_payload() {
            const errors = {};

            assign_error(errors, "first_name", validate_person_name(this.form.first_name, "First name"));
            assign_error(errors, "last_name", validate_person_name(this.form.last_name, "Last name"));
            assign_error(errors, "phone", validate_phone_number(this.form.phone));

            if (this.is_managed_user_edit && !this.is_selected_user_admin() && !this.form.property_ids.length) {
                errors.property_ids = "Select at least one property for this user.";
            }

            return errors;
        },

        validate_field(field_name, show_required_error = true) {
            const validators = {
                first_name: {
                    value: this.form.first_name,
                    label: "First name",
                    validator: validate_person_name
                },
                last_name: {
                    value: this.form.last_name,
                    label: "Last name",
                    validator: validate_person_name
                },
                phone: {
                    value: this.form.phone,
                    label: "Phone number",
                    validator: validate_phone_number
                }
            };
            const field = validators[String(field_name)];

            if (!field) return;

            this.field_errors[String(field_name)] = this.get_field_validation_error(
                field.value,
                field.label,
                field.validator,
                show_required_error
            );
        },

        get_field_validation_error(value, label, validator, show_required_error = true) {
            if (!show_required_error && !String(value || "").trim()) {
                return "";
            }

            return validator(value, label);
        },

        is_selected_user_admin() {
            return is_admin_user(this.get_selected_user_from_storage());
        },

        async update_user() {
            const validation_errors = this.validate_user_payload();
            const profile = get_user_profile();
            const user_id = this.is_managed_user_edit
                ? this.selected_user_id
                : this.get_selected_profile_id(profile);
            const safe_user_id = this.parse_user_id(user_id);
            const auth_token = get_token();
            const user_update_url = this.is_managed_user_edit
                ? this.get_endpoint_url(this.user_management_url, safe_user_id, "update")
                : this.get_endpoint_url(this.view_user_url, safe_user_id, "update");
            const profile_update_url = this.get_endpoint_url(this.update_user_url, safe_user_id, "update");

            if (Object.keys(validation_errors).length) {
                this.field_errors = validation_errors;
                this.form_error = "Please correct the highlighted fields.";
                this.$nextTick(this.focus_first_invalid_field);
                return false;
            }

            if (!safe_user_id || !auth_token || !user_update_url) {
                this.form_error = "Unable to save user details.";
                return false;
            }

            if (this.is_managed_user_edit && !this.user_management_url) {
                console.warn("VUE_APP_USER_MANAGEMENT_URL is not configured.");
                this.form_error = "Unable to save user details.";
                return false;
            }

            if (!this.is_managed_user_edit && !this.view_user_url) {
                console.warn("VUE_APP_VIEW_USER_URL or VUE_APP_USER_MANAGEMENT_URL is not configured.");
                this.form_error = "Unable to save user details.";
                return false;
            }

            this.is_saving = true;
            this.form_error = "";

            try {
                const user_request = new Request(user_update_url, {
                    method: "POST",
                    headers: this.get_auth_headers(true),
                    body: JSON.stringify({
                        "first_name": this.form.first_name,
                        "last_name": this.form.last_name
                    })
                });
                const user_response = await fetch(user_request);
                await this.get_response_data(user_response, "Unable to save user.");

                if (this.is_managed_user_edit) {
                    await sync_user_property_assignments(
                        safe_user_id,
                        this.form.property_ids,
                        this.get_auth_headers(true)
                    );
                }

                if (profile_update_url) {
                    const profile_request = new Request(profile_update_url, {
                        method: "POST",
                        headers: this.get_auth_headers(true),
                        body: JSON.stringify({
                            "phone_number": this.form.phone || ""
                        })
                    });
                    const profile_response = await fetch(profile_request);
                    await this.get_response_data(profile_response, "Unable to save user profile.");
                }

                if (!this.is_managed_user_edit) {
                    set_user_profile({
                        ...(profile || {}),
                        first_name: this.form.first_name,
                        last_name: this.form.last_name,
                        email: this.form.email,
                        phone_number: this.form.phone
                    });
                }

                return true;
            } catch (error) {
                this.form_error = error.message || "Unable to save user details.";
                return false;
            } finally {
                this.is_saving = false;
            }
        },

        async saveProfile() {
            this.field_errors = {};
            this.form_error = "";

            const validation_errors = this.validate_user_payload();

            if (Object.keys(validation_errors).length) {
                this.field_errors = validation_errors;
                this.form_error = "Please fix the highlighted fields before saving.";
                this.$nextTick(this.focus_first_invalid_field);
                return;
            }

            const did_save = await this.update_user();

            if (did_save) {
                this.$router.push(this.get_return_path());
            }
        },

        focus_first_invalid_field() {
            const invalid_fields = [
                { error: this.field_errors.first_name, ref: this.$refs.first_name_input },
                { error: this.field_errors.last_name, ref: this.$refs.last_name_input },
                { error: this.field_errors.phone, ref: this.$refs.phone_input },
                { error: this.field_errors.property_ids, ref: this.$refs.propertyButtons?.[0] }
            ];

            const first_invalid_field = invalid_fields.find((field) => field.error && field.ref);

            first_invalid_field?.ref.focus();
        }
    }
};
